<template>
  <div class="w-100 p-2 mt-2">
    <page-header>
      <b-icon-clock-history class="mr-1"></b-icon-clock-history>
      {{ $t('page.title.transactions_history') }}
    </page-header>
    <page-card>
      <template v-slot:header>
        {{ $t('title.filters') }}
      </template>
      <template v-slot:content>
        <div class="sygni-grid">
          <div class="row">
            <div class="col-4">
              <filter-input
                  :filter-query="filterQuery"
                  filter-name="phrase"
                  :label="$t('filters.label.transaction_history.phrase')"
                  :placeholder="$t('filters.placeholder.transaction_history.phrase')">
              </filter-input>
            </div>
            <div class="col-4">
              <filter-date-range-picker
                  :filter-query="filterQuery"
                  :locale="{firstDay: 1, format: 'yyyy-mm-dd'}"
                  :auto="true"
                  :linked="false"
                  :time-picker="true"
                  filter-name="createdAt"
                  open-direction="left"
                  :label="$t('filters.label.transaction_date')">
              </filter-date-range-picker>
            </div>
            <div class="col-4">
              <filter-input
                  :filter-query="filterQuery"
                  :default-filter="$route.query.transactionId"
                  :filter-operator="'like'"
                  filter-name="transactionId"
                  :label="$t('filters.label.transaction_id')"
              >
              </filter-input>
            </div>
          </div> <!-- #row-->

          <div class="row">
            <div class="col-4">
              <filter-select
                  :filter-query="filterQuery"
                  :options="filterOptions.transactionType"
                  :multiple="false"
                  options-label="text"
                  options-track-by="value"
                  filter-name="transactionType"
                  :label="$t('filters.label.transaction_type')"
                  :placeholder="$t('filters.placeholder.transaction_type')">
              </filter-select>
            </div>
            <div class="col-4">
              <filter-input-with-operator
                  :filter-query="filterQuery"
                  :operator-options="filterOptions.comparisonOperator"
                  :operator-multiple="false"
                  :default-operator="filterOptions.comparisonOperator[0]"
                  :restrict-input="true"
                  restriction-type="decimals"
                  filter-name="quantity"
                  :label="$t('filters.label.quantity')"
              >
              </filter-input-with-operator>
            </div>
            <div class="col-4">
              <filter-select
                  :filter-query="filterQuery"
                  :options="filterOptions.currency"
                  ref="currency-ref"
                  :multiple="false"
                  options-label="text"
                  options-track-by="value"
                  filter-name="walletCurrency"
                  :label="$t('filters.label.wallet_currency')"
                  :placeholder="$t('filters.placeholder.wallet_currency')">
              </filter-select>
            </div>
          </div> <!-- #row-->

          <div class="row">
            <div class="col-4">
              <filter-input-with-operator
                  :filter-query="filterQuery"
                  :operator-options="filterOptions.comparisonOperator"
                  :operator-multiple="false"
                  :default-operator="filterOptions.comparisonOperator[0]"
                  :restrict-input="true"
                  restriction-type="decimals"
                  filter-name="transactionAmount"
                  :label="$t('filters.label.transaction_amount')"
              >
              </filter-input-with-operator>
            </div>
            <div class="col-4">
              <filter-select
                  :filter-query="filterQuery"
                  :options="filterOptions.paymentStatus"
                  :multiple="false"
                  options-label="text"
                  options-track-by="value"
                  filter-name="transactionStatus"
                  :label="$t('filters.label.payment_status')"
                  :placeholder="$t('filters.placeholder.payment_status')">
              </filter-select>
            </div>
            <div class="col-4">
              <filter-select
                  :filter-query="filterQuery"
                  :options="filterOptions.paymentMethod"
                  :multiple="false"
                  options-label="text"
                  options-track-by="value"
                  filter-name="paymentMethod"
                  :label="$t('filters.label.payment_method')"
                  :placeholder="$t('filters.placeholder.payment_method')">
              </filter-select>
            </div>
          </div> <!-- #row-->
        </div> <!-- #filters-->
      </template>

      <template v-slot:footer>
        <b-button @click="resetFilters()" variant="secondary">{{$t('filters.btn.clear')}}</b-button>
        <b-button @click="applyFilters()" variant="primary">{{$t('filters.btn.apply')}}</b-button>
      </template>
    </page-card>

    <page-card>
      <template v-slot:header>
        {{ $t('title.list') }}
      </template>
      <template v-slot:content>
      <div class="sygni-grid">

        <grid-pagination :total-count="totalCount"
                         :per-page="perPage"
                         :current-page="currentPage"
                         @load-items="paginationReload"></grid-pagination>

        <div class="table-responsive">
          <b-table striped show-empty
                   :empty-text="$t('table.no_results')"
                   :busy="isBusy"
                   hover
                   head-variant="dark"
                   outlined
                   :sort-by.sync="orderColumn"
                   :sort-desc.sync="orderDirection"
                   :no-local-sorting="true"
                   :items="items"
                   :fields="fields">
            <template #head(alias)="data">
              <b-checkbox
                  @change="toggleAllCheckboxes($event, data)">
              </b-checkbox>
            </template>

            <template #cell(alias)="data">
              <b-checkbox v-model="selectedRows"
                          :key="data.item.alias"
                          :value="data.item.alias">
              </b-checkbox>
            </template>

            <template #cell(languages)="data">
              <div class="table-flag--wrapper">
                <div v-for="(lang, index) in data.value" :key="index">
                    <span>
                      <img :src="`~@/assets/flags/${lang}.svg`" class="table-flags"> {{ lang.toUpperCase() }}
                    </span>
                </div>
              </div>
            </template>

            <template #cell(aliasId)="data">
              <div>{{data.item.alias}}</div>
            </template>

            <template #cell(createdAt)="data">
              {{ data.value | dateFormat('YYYY-MM-DD HH:mm:ss') }}
            </template>

            <template #cell(transactionId)="data">
              {{ data.value | shortId }}
            </template>

            <template #cell(transactionAmount)="data">
              {{ data.value | numberFormat }}
            </template>

            <template #cell(categories)="data">
              <div class="table-categories--wrapper" v-for="(cat, index) in data.value" :key="index">
                <b-badge variant="light">{{ cat }}</b-badge>
              </div>
            </template>

            <template #cell(walletCurrency)="data">
              {{  $t(data.value) }}
            </template>

            <template #cell(paymentMethod)="data">
              {{  $t(data.value) }}
            </template>

            <template #cell(transactionStatus)="data">
              {{  $t(data.value) }}
            </template>

            <template #cell(paymentStatus)="data">
              <b-badge pill variant="warning" v-if="data.value === 'PENDING'">{{$t(data.value)}}</b-badge>
              <b-badge pill variant="danger" v-else-if="data.value === 'REJECTED'">{{$t(data.value)}}</b-badge>
              <b-badge pill variant="success" v-else>{{$t(data.value)}}</b-badge>
            </template>

            <template #cell(transactionType)="data">
              <b-badge pill variant="primary" v-if="data.value === 'PURCHASE'">{{$t(data.value)}}</b-badge>
              <b-badge pill variant="danger" v-else>{{$t(data.value)}}</b-badge>
            </template>

            <template #cell(answers)="data">
              <div class="table--answers">
                <div v-for="(ans, index) in data.value" :key="index">
                  <b-badge :variant="data.item.correctAnswer === (index+1) ? 'success' : ''">
                    {{ ans }}
                  </b-badge>
                </div>
              </div>
            </template>

            <template #cell(actions)="data">
              <div class="table-action--cell">
                <template v-if="data.item.transactionType === 'RESALE' && data.item.transactionStatus === 'WAITING_FOR_ACCEPTANCE'">
                  <b-button variant="success" size="sm"
                            @click="confirmTransaction(data.item)">
                    {{ $t('table.btn.confirm') }}
                  </b-button>
                  <b-button variant="danger" size="sm"
                            @click="cancelTransaction(data.item)">
                    {{ $t('table.btn.cancel') }}
                  </b-button>
                </template>
              </div>
            </template>

            <template #table-busy>
              <div class="text-center text-danger my-2">
                <b-spinner class="align-middle"></b-spinner>
              </div>
            </template>

          </b-table>
        </div> <!-- #table-responsive-->

        <grid-pagination :total-count="totalCount"
                         :per-page="perPage"
                         :current-page="currentPage"
                         @load-items="paginationReload"></grid-pagination>

      </div>
      </template>
      <template v-slot:footer>

      </template>
    </page-card>
  </div>
</template>

<script>

import axios from "axios";
import alertify from "alertifyjs";
import FilterDateRangePicker from "../../../components/grid/filters/FilterDateRangePicker";
import FilterInputWithOperator from "../../../components/grid/filters/FilterInputWithOperator";
import FilterInput from "../../../components/grid/filters/FilterInput";
import FilterSelect from "../../../components/grid/filters/FilterSelect";
import GridMixin from "../../../components/grid/GridMixin";
import GridPagination from "../../../components/grid/grid-pagination/GridPagination";
import PageHeader from "../../../components/page/PageHeader";
import PageCard from "../../../components/page/PageCard";

export default {
  mixins: [GridMixin],
  components: {
    PageCard,
    PageHeader,
    FilterSelect,
    FilterInput,
    FilterInputWithOperator,
    FilterDateRangePicker,
    GridPagination
  },
  data() {
    return {
      gridGetUrl: '/api/transaction/',
      deleteActionUrl: '/api/dictionary/',
      showDeleted: false,
      orderColumn: null,
      orderDirection: true,
      fields: [
        {key: 'transactionId', label: this.$t('table.header.transaction_id')},
        {key: 'createdAt', label: this.$t('table.header.transaction_date'), sortable: true},
        {key: 'userNick', label: this.$t('table.header.nick_name'), sortable: true},
        {key: 'userEmail', label: this.$t('table.header.email'), sortable: true},
        {key: 'transactionType', label: this.$t('table.header.transaction_type'), sortable: true},
        {key: 'quantity', label: this.$t('table.header.quantity'), sortable: true},
        {key: 'walletCurrency', label: this.$t('table.header.wallet_currency')},
        {key: 'transactionAmount', label: this.$t('table.header.amount'), sortable: true},
        {key: 'transactionCurrency', label: this.$t('table.header.currency')},
        {key: 'transactionStatus', label: this.$t('table.header.payment_status'), sortable: true},
        {key: 'paymentMethod', label: this.$t('table.header.payment_method')},
        {key: 'actions', label: this.$t('table.header.actions')}
      ],
      selectOptions: {
        bulkActions: [
          {value: null, text: this.$t('table.bulk.actions')},
          {value: 'delete', text: this.$t('table.bulk.actions.delete')},
        ],
      },
      filterOptions: {
        comparisonOperator: [
          {value: 'eq', text: this.$t('EQUALS')},
          {value: 'gte', text: this.$t('GREATER_THAN')},
          {value: 'lte', text: this.$t('LOWER_THAN')},
          {value: 'btw', text: this.$t('BETWEEN')},
        ],
        transactionType: [
          {value: 'PURCHASE', text: this.$t('PURCHASE')},
          {value: 'RESALE', text: this.$t('RESALE')},
        ],
        currency: [
          {value: 'FUN_COINS', text: this.$t('FUN_COINS')},
          {value: 'QUIZ_COINS', text: this.$t('QUIZ_COINS')},
        ],
        paymentStatus: [
          {value: 'PAYMENT_REJECTED', text: this.$t('PAYMENT_REJECTED')},
          {value: 'FAILURE', text: this.$t('FAILURE')},
          {value: 'PAID', text: this.$t('PAID')},
          {value: 'WAITING_FOR_PAYMENT', text: this.$t('WAITING_FOR_PAYMENT')},
          {value: 'WAITING_FOR_ACCEPTANCE', text: this.$t('WAITING_FOR_ACCEPTANCE')},
          {value: 'SUCCESS', text: this.$t('SUCCESS')},
        ],
        paymentMethod: [
          {value: 'MANUAL_PAYMENT_METHOD', text: this.$t('MANUAL_PAYMENT_METHOD')},
          {value: 'PAY_PRO_PAYMENT_METHOD', text: this.$t('PAY_PRO_PAYMENT_METHOD')},
        ],
      },
      items: [],
    };
  },
  mounted() {
    this.$on('refresh-dict', () => {
      this.loadItems();
    })
  },
  methods: {
    createFilterQuery() {
      let filter = '';
      Object.keys(this.filterQuery).forEach(key => {
        if(key === 'created_at'){
          let dateFilters = this.filterQuery[key].replace('[created_at][0][value]','[dateFrom][0][value]');
          dateFilters = dateFilters.replace('[created_at][1][value]','[dateTo][0][value]');
          dateFilters = dateFilters.replace('[created_at][0][operator]','[dateFrom][0][operator]');
          dateFilters = dateFilters.replace('[created_at][1][operator]','[dateTo][0][operator]');
          filter += dateFilters;
          return;
        }
        filter += this.filterQuery[key];

        if (key === 'phrase') {
          filter += '&filters[phrase][0][operator]=like'
        }

        if (key === 'walletCurrency') {
          filter += '&filters[walletCurrency][0][operator]=in'
        }
      })
      return filter;
    },
    confirmTransaction(item) {
      const url = `/api/resale/quiz-coin-resale/${item.transactionId}/acceptance`;

      this.$bvModal.msgBoxConfirm('Transaction: '+ this.$options.filters.shortId(item.transactionId), {
        title: 'Are you sure to confirm transaction?',
        okTitle: 'Confirm',
        cancelTitle: 'Cancel',
        okVariant: 'success',
      })
      .then(value => {
        if (value) {
          axios.put(url)
            .then(() => {
              alertify.success(this.$t('alert.success'));
              setTimeout(() => {
                this.loadItems();
              }, 1000);
            })
            .catch(error => {
              alertify.error(this.$t('alert.error'));
              console.log(error);
            });
        }
      });
    },
    cancelTransaction(item) {
      const url = `/api/resale/quiz-coin-resale/${item.transactionId}/rejection`;

      this.$bvModal.msgBoxConfirm('Transaction: '+ this.$options.filters.shortId(item.transactionId), {
        title: 'Are you sure to reject transaction?',
        okTitle: 'Confirm',
        cancelTitle: 'Cancel',
        okVariant: 'warning',
      })
      .then(value => {
        if (value) {
          axios.put(url)
            .then(() => {
              alertify.success(this.$t('alert.success'));
              setTimeout(() => {
                this.loadItems();
              }, 1000);
            })
            .catch(error => {
              alertify.error(this.$t('alert.error'));
              console.log(error);
            });
        }
      });
    },
    loadItems() {
      this.isBusy = true;
      let _that = this;
      let offset = this.perPage * (this.currentPage - 1);
      let fullUrl = this.gridGetUrl + '?limit='
          + this.perPage
          + '&offset=' + offset
          + this.createSortQuery() + this.createFilterQuery()
      axios.get(fullUrl)
          .then(function (response) {
            _that.items = response.data.items;
            _that.totalCount = response.data.totalCount;
            _that.isBusy = false;
          })
          .catch(error => {
            console.log(error);
          });
    },
    toggleAllCheckboxes: function (checked) {
      this.selectedRows = [];
      if (checked) {
        let _that = this;
        this.items.forEach((value) => {
          _that.selectedRows.push(value.id);
        })
      }
    },
    goTo(item) {
      this.$router.push({name: 'dictionary-entry', params: {entry: item.alias}});
    },
    edit(item) {
      this.$router.push({name: 'dictionary-entry', params: {entry: item.alias}});
    },
    deleteItem: function (item) {
      let _that = this;
      this.$bvModal.msgBoxConfirm('Dictionary: '+ item.alias, {
        title: 'Are you sure to delete dictionary?',
        okTitle: 'Delete',
        cancelTitle: 'Cancel',
        okVariant: 'danger',
      })
          .then(value => {
            if (value) {
              axios.delete(`/api/dictionary/${item.alias}`)
                  .then(function () {
                    _that.loadItems();
                  })
                  .catch(error => {
                    console.log(error);
                  });
            }
          });
    }
  }
}
</script>
